import React, {useState} from 'react';
import Box from '@mui/material/Box';
import {APP_HEADER_HEIGHT, FOOTER_HEIGHT, WHITE_100} from '../App';
import {useLDClient} from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
  Grid,
  Stack,
  Button,
  Table,
  TableContainer,
  TableBody,
  Typography,
  TableCell,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import {useDispatch, useSelector} from 'react-redux';
import {useUpdateOrgMutation} from '../data/api';
import {usePopupState} from 'material-ui-popup-state/hooks';
import {bindTrigger} from 'material-ui-popup-state';
import {ChevronDownIcon} from 'lucide-react';
import TableHeaderDateCell from './financialStatements/TableHeaderDateCell';
import {GREY_BACKGROUND_HOVER, GREY_BORDER} from '../theme';
import {
  CascadingMenu,
  MenuItem,
} from '../components/CascadingMenu/CascadingMenu';
import {setOrgConfig} from '../data/session';
import {useNavigate} from 'react-router-dom';

const tableHeaderCellStyles = {
  padding: '0.5rem 0.5rem 0.5rem calc(0.5rem + 20px)',
  textAlign: 'center',
  borderBottom: 2,
  width: '128px',
  marginBottom: '-1px',
};

export const RoundToDropdown = ({refetch}) => {
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'roundToDropdown',
  });
  const [isRefetching, setIsRefetching] = useState(false);

  const roundToOptions = ['ones', 'thousands', 'millions'];

  const dispatch = useDispatch();
  const {orgConfig} = useSelector((state) => state.session);
  const [updateOrg] = useUpdateOrgMutation();

  const handleClick = async (newValue) => {
    if (newValue !== orgConfig?.roundNumbersTo && !_.isEmpty(orgConfig)) {
      const newOrgConfigPayload = {
        ...orgConfig,
        roundNumbersTo: newValue,
      };
      dispatch(setOrgConfig({orgConfig: newOrgConfigPayload}));
      setIsRefetching(true);
      await updateOrg({...orgConfig, roundNumbersTo: newValue});
      await refetch();
      setIsRefetching(false);
    }
    popupState.close();
  };
  return (
    <div>
      <Button
        {...bindTrigger(popupState)}
        variant="text"
        sx={{color: '#0F1E24'}}
      >
        <div style={{display: 'flex', alignItems: 'center'}}>
          <Typography variant="caption">
            Round numbers to:{' '}
            <Typography
              variant="caption"
              sx={{textTransform: 'capitalize', padding: '0 4px'}}
              mr={0.5}
            >
              {orgConfig?.roundNumbersTo || 'ones'}
            </Typography>
          </Typography>
          {isRefetching ? (
            <CircularProgress variant="indeterminate" size={'0.75rem'} />
          ) : (
            <ChevronDownIcon size={12} />
          )}
        </div>
      </Button>
      <CascadingMenu
        popupState={popupState}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        width="248px"
      >
        <Grid p={1}>
          {roundToOptions.map((option, index) => (
            <MenuItem key={index} onClick={() => handleClick(option)}>
              <span style={{textTransform: 'capitalize'}}>{option}</span>
            </MenuItem>
          ))}
        </Grid>
      </CascadingMenu>
    </div>
  );
};

RoundToDropdown.propTypes = {
  refetch: () => {},
};

const ReportTableComponent = ({
  children,
  visible = true,
  period,
  comparePeriod,
  report,
  meta,
  refetch,
}) => {
  const fluxAnalysisActive = useSelector(
    (state) => state.reports.fluxAnalysisActive
  );
  const {reportingPeriodMap} = useSelector((state) => state.periods);
  const ldClient = useLDClient();
  const navigate = useNavigate();
  if (!visible || _.isNil(period) || _.isEmpty(period)) {
    return;
  }

  let _generatedPeriod;
  if (!_.isNil(meta) && _.has(meta, 'generatedPeriod')) {
    _generatedPeriod = reportingPeriodMap[meta.generatedPeriod];
  }

  return (
    <Box
      position="sticky"
      top={0}
      maxHeight={`calc(100vh - ${APP_HEADER_HEIGHT} - ${FOOTER_HEIGHT} - 11rem)`}
      sx={{overflowY: 'auto', borderBottom: `1px solid ${GREY_BORDER}`}}
    >
      <Stack
        direction="column"
        position="sticky"
        bgcolor={WHITE_100}
        top={0}
        zIndex={1}
        sx={{overflow: 'hidden'}}
      >
        <Stack
          bgcolor={GREY_BACKGROUND_HOVER}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          p={1.5}
          sx={{
            borderColor: GREY_BORDER,
            borderStyle: 'solid',
            borderWidth: '1px 1px 0 1px',
            borderTopRightRadius: 8,
            borderTopLeftRadius: 8,
          }}
          spacing={1}
        >
          <RoundToDropdown refetch={refetch} />
          {report?.reportType === 'INDIRECT_CASHFLOW' &&
            ldClient.allFlags()['indirect-cash-flow'] && (
              <Button
                variant="outlined"
                onClick={() => {
                  navigate('/statements/indirect-cashflow-workbook');
                }}
                sx={{
                  backgroundColor: 'white',
                  color: '#0F1E24',
                  borderColor: '#CDD3D3',
                }}
              >
                Review Workbook
              </Button>
            )}
        </Stack>
        <Stack
          direction="row"
          bgcolor={WHITE_100}
          justifyContent="flex-end"
          border={`1px solid ${GREY_BORDER}`}
        >
          <TableHeaderDateCell
            period={period}
            generatedPeriod={_generatedPeriod}
            reportType={report?.reportType}
            sx={tableHeaderCellStyles}
          />
          {!_.isNil(comparePeriod) && (
            <TableHeaderDateCell
              period={comparePeriod}
              generatedPeriod={_generatedPeriod}
              reportType={report?.reportType}
              sx={tableHeaderCellStyles}
            />
          )}
          {fluxAnalysisActive && (
            <>
              <TableCell sx={tableHeaderCellStyles} align="right" scope="row">
                <Typography
                  fontSize={'0.875rem'}
                  fontWeight={600}
                  lineHeight={'1.25rem'}
                >
                  $ change
                </Typography>
              </TableCell>
              <TableCell sx={tableHeaderCellStyles} align="right" scope="row">
                <Typography
                  fontSize={'0.875rem'}
                  fontWeight={600}
                  lineHeight={'1.25rem'}
                >
                  % change
                </Typography>
              </TableCell>
              <TableCell
                sx={{...tableHeaderCellStyles, width: 200}}
                align="left"
                scope="row"
              >
                <Typography
                  fontSize={'0.875rem'}
                  fontWeight={600}
                  lineHeight={'1.25rem'}
                >
                  Explanation
                  <Typography
                    fontSize={'0.65rem'}
                    fontWeight={500}
                    color="gray"
                  >
                    * For changes over 5%
                  </Typography>
                </Typography>
              </TableCell>
            </>
          )}
        </Stack>
      </Stack>
      <TableContainer
        sx={{
          borderLeft: `1px solid ${GREY_BORDER}`,
          borderRight: `1px solid ${GREY_BORDER}`,
        }}
      >
        <Table className="report-table">
          <TableBody>{children}</TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

ReportTableComponent.propTypes = {
  visible: PropTypes.bool,
  period: PropTypes.shape({
    endDate: PropTypes.string.isRequired,
    periodType: PropTypes.string.isRequired,
  }).isRequired,
  comparePeriod: PropTypes.shape({
    endDate: PropTypes.string.isRequired,
    periodType: PropTypes.string.isRequired,
  }),
  meta: PropTypes.shape({
    generatedPeriod: PropTypes.string,
  }),
  children: PropTypes.node.isRequired,
  report: PropTypes.object,
  refetch: PropTypes.func.isRequired,
};

export default ReportTableComponent;
